import {
  useGetInvoicesSuspense,
  useGetUsersSuspense,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makePath } from 'utils/routes';

import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';

import { BillingToggle } from './BillingToggle';
import { FILLER_BORDER, GRID_CONTAINER, TILE_BORDER } from './constants';
import { CreateUserModal } from './CreateUserModal';
import { EmptyGridTile, GridTile } from './GridTile';
import { UserColorDot } from './UserColorDot';

type UserRow = {
  id: string;
  externalId: string;
  invoiceCount: number;
};

const userSchema: ColumnDefinition<UserRow>[] = [
  {
    title: 'ID',
    colspan: 'col-span-4',
    format: (row) => (
      <div className="flex items-center gap-x-f1">
        <UserColorDot seed={row.externalId} />
        <span>{row.externalId}</span>
      </div>
    ),
  },
  {
    title: 'Invoices',
    justify: 'right',
    colspan: 'col-span-2',
    format: (row) => (
      <span>{row.invoiceCount > 0 ? String(row.invoiceCount) : '—'}</span>
    ),
  },
];

const ColumnHeaders = () => (
  <div className="grid grid-cols-6 gap-x-[1ch] px-f1 items-center h-f4 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-4">ID</div>
    <div className="col-span-2 text-right">Invoices</div>
  </div>
);

const ViewToggle = ({
  viewMode,
  setViewMode,
  readOnly,
}: {
  viewMode?: 'grid' | 'list';
  setViewMode?: (m: 'grid' | 'list') => void;
  readOnly?: boolean;
}) => (
  <BillingToggle
    readOnly={readOnly}
    initialActiveButton={viewMode === 'list' ? 'right' : 'left'}
    onLeftClick={() => setViewMode?.('grid')}
    onRightClick={() => setViewMode?.('list')}
    leftLabel={<Icon type="gridView" size="sm" />}
    rightLabel={<Icon type="listView" size="sm" />}
    buttonPadding="py-f0"
    buttonHeight="h-fit"
  />
);

const UserGridTile = ({ user, href }: { user: UserRow; href: string }) => (
  <GridTile
    href={href}
    icon={<UserColorDot seed={user.externalId} size={24} hoverAnimate />}
    primary={user.externalId}
  />
);

const EmptyUserGridTile = () => (
  <EmptyGridTile
    icon={<div className="w-f2 h-f2 rounded-full shrink-0 bg-main-200" />}
    label="No Users Yet"
  />
);

const userEmptyValue = {
  ID: 'No Users Yet',
  Invoices: '—',
};

const SKELETON_MAX_TILES = 12;

type Props = {
  workspaceId: string;
};

const UsersListPageLoading = ({ workspaceId: _workspaceId }: Props) => {
  const [visibleTiles, setVisibleTiles] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleTiles((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_TILES) {
          directionRef.current = -1;
          return SKELETON_MAX_TILES;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <PaymentViewLayout
      title="Users"
      secondaryAction={{ title: 'Edit', onClick: () => {}, disabled: true }}
      primaryAction={{ title: 'New', onClick: () => {}, disabled: true }}
    >
      <div className="flex flex-col w-full min-h-0">
        <div className="sticky top-0 z-10 shrink-0">
          <div className="flex items-end w-full">
            <div className="flex-1 border-b-[0.5px] border-main-300 py-f1 text-xmd min-h-[2.5rem] [line-height:1]" />
            <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-300 px-f1 self-stretch">
              <ViewToggle readOnly />
            </div>
          </div>
        </div>
        <div className={GRID_CONTAINER}>
          <div className={clsx('aspect-[8/9]', TILE_BORDER)} />
          {/* eslint-disable react/no-array-index-key */}
          {Array.from({ length: visibleTiles }).map((__, i) => (
            <div
              key={`skeleton-${i}`}
              className={clsx('aspect-[8/9]', TILE_BORDER)}
            />
          ))}
          {Array.from({ length: 8 }).map((__, i) => (
            <div key={`filler-${i}`} className={FILLER_BORDER} />
          ))}
          {/* eslint-enable react/no-array-index-key */}
        </div>
      </div>
    </PaymentViewLayout>
  );
};

const UsersListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Users');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const { data: usersData } = useGetUsersSuspense();
  const { data: invoicesData } = useGetInvoicesSuspense();

  const invoiceCountByUser = useMemo(() => {
    const counts: Record<string, number> = {};
    invoicesData.data.forEach((invoice) => {
      const userIds = new Set(
        (invoice.line_items ?? []).map((item) => item.user_id).filter(Boolean)
      );
      userIds.forEach((userId) => {
        counts[userId] = (counts[userId] ?? 0) + 1;
      });
    });
    return counts;
  }, [invoicesData.data]);

  const users: UserRow[] = useMemo(
    () =>
      usersData.data.map((u) => ({
        id: u.id,
        externalId: u.external_id,
        invoiceCount: invoiceCountByUser[u.external_id] ?? 0,
      })),
    [usersData.data, invoiceCountByUser]
  );

  const getPath = useCallback(
    (user: UserRow) =>
      makePath(['w', workspaceId, 'payments', 'users', user.externalId], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreate = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  return (
    <>
      <PaymentViewLayout
        title="Users"
        primaryAction={{
          title: 'New',
          onClick: handleCreate,
          'data-testid': 'create-user-button',
        }}
      >
        <div className="flex flex-col w-full h-full min-h-0">
          <div className="sticky top-0 z-10 shrink-0">
            <div className="group flex items-end w-full">
              <div className="flex-1 border-b-[0.5px] border-main-300 py-f1 text-xmd min-h-[2.5rem] [line-height:1]" />
              <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-300 px-f1 self-stretch">
                <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
              </div>
            </div>
            {viewMode === 'list' && <ColumnHeaders />}
          </div>
          {viewMode === 'list' ? (
            <NavTableV2
              textSize="xmd"
              data={users}
              emptyValue={userEmptyValue}
              getPath={getPath}
              schema={userSchema}
              showHeader={false}
              stickyHeader={false}
              rowHeight="fixed"
            />
          ) : (
            <div className={GRID_CONTAINER}>
              {users.length === 0 ? (
                <EmptyUserGridTile />
              ) : (
                users.map((user) => (
                  <UserGridTile
                    key={user.id}
                    user={user}
                    href={getPath(user)}
                  />
                ))
              )}
              {Array.from({ length: 8 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`filler-${i}`} className={FILLER_BORDER} />
              ))}
            </div>
          )}
        </div>
      </PaymentViewLayout>
      {showCreateModal && (
        <CreateUserModal
          onClose={handleCloseModal}
          onCreated={handleCloseModal}
        />
      )}
    </>
  );
};

export const UsersListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<UsersListPageLoading workspaceId={workspaceId} />}>
    <UsersListPageContent workspaceId={workspaceId} />
  </Suspense>
);
